/**
 * Thin REST client over a proxy fetcher.
 *
 * Stateless: it holds no proxyUrl/apiUrl/scopedToken/projectCode of its own.
 * Everything about the transport — the proxy URL scheme and the auth params —
 * lives in `utils/proxyFetcher.js`, and the fetcher resolves its config on
 * every call, so a rotated token is picked up with no re-configuration here.
 *
 *   const api = new ApiClient({ fetcher });
 *   await api.get('/api/111/documents');
 *
 * @typedef {import('./proxyFetcher.js').ProxyFetcher} ProxyFetcher
 */
class ApiClient {
  /**
   * @param {{ fetcher: ProxyFetcher }} options
   */
  constructor({ fetcher } = {}) {
    this.fetcher = fetcher;
  }

  /**
   * @param {string} method
   * @param {string} path
   * @param {any} [data]                body for POST/PUT/PATCH
   * @param {Function} [callback]       receives the Response for file
   *   responses, the parsed body otherwise
   * @param {boolean} [isFileResponse]  return an ArrayBuffer instead of JSON
   */
  async request(method, path, data = null, callback = null, isFileResponse = false) {
    const headers = {
      'Content-Type': 'application/json',
      // axios's default Accept — kept so the request on the wire is identical
      // to the pre-fetch client (verified against the transport baseline)
      'Accept': isFileResponse ? '*/*' : 'application/json, text/plain, */*'
    };

    const init = { method, headers };

    if (data && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
      init.body = JSON.stringify(data);
    }

    try {
      const response = await this.fetcher(path, init);

      // axios threw on any non-2xx; keep that contract, and carry the status
      // so callers can tell an outage from an auth failure
      if (!response.ok) {
        const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
        error.status = response.status;
        error.statusText = response.statusText;
        error.response = response;
        throw error;
      }

      if (isFileResponse) {
        const buffer = await response.arrayBuffer();
        if (callback) {
          await callback(response);
        }
        return buffer;
      }

      // A 204 or an otherwise empty body is not valid JSON — axios surfaced
      // those as an empty value rather than throwing
      const text = await response.text();
      const parsed = text ? JSON.parse(text) : null;

      if (callback) {
        await callback(parsed);
      }

      return parsed;
    } catch (error) {
      console.error(`Error in ${method} request:`, error);
      throw error;
    }
  }


  async get(path, callback, isFileResponse) {
    return this.request('GET', path, null, callback, isFileResponse);
  }

  async post(path, data, callback, isFileResponse) {
    return this.request('POST', path, data, callback, isFileResponse);
  }

  async put(path, data, callback, isFileResponse) {
    return this.request('PUT', path, data, callback, isFileResponse);
  }

  async patch(path, data, callback, isFileResponse) {
    return this.request('PATCH', path, data, callback, isFileResponse);
  }
}

export default ApiClient;
